// Venue documents store — owns the polling lifecycle for in-flight
// extractions so the polling survives component unmount when the operator
// switches venues mid-extraction.
//
// Per plan U9: polling lives in a zustand store, NOT in the component,
// per the project convention. AbortController cancels in-flight fetches
// on explicit teardown. 5-minute hard timeout per document — after that
// the frontend gives up; server-side processing may still complete and
// a future page refresh will pick up the final state.

import { create } from "zustand";
import { api, ApiError } from "@/lib/api";
import type {
  VenueDocument,
  VenueDocumentDetail,
  ExtractionStatus,
} from "@/lib/types";

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 5 * 60_000;
const TERMINAL_STATUSES: ReadonlySet<ExtractionStatus> = new Set<ExtractionStatus>([
  "complete",
  "failed",
  "no_data",
  "applied",
]);

interface ActivePoll {
  controller: AbortController;
  intervalId: number;
  timeoutId: number;
  startedAt: number;
}

interface VenueDocumentsStore {
  // Documents keyed by venue_id → docId → VenueDocument. Updated by both
  // the initial list fetch and by the polling cycle.
  docsByVenue: Record<string, Record<string, VenueDocument>>;
  // Detail (extraction_payload + diff) keyed by document_id. Populated
  // when an extraction completes — the diff-review screen reads from
  // here when it first mounts to avoid an extra fetch.
  detailByDoc: Record<string, VenueDocumentDetail>;
  // Per-document AbortController + interval IDs for polling cleanup.
  polls: Record<string, ActivePoll>;

  // Hydrate from a fresh list.
  setDocsForVenue: (venueId: string, docs: VenueDocument[]) => void;
  // Insert/update a single doc (after upload, after status change).
  upsertDoc: (doc: VenueDocument) => void;
  // Remove from local state (after soft-delete / purge).
  dropDoc: (venueId: string, documentId: string) => void;

  // Start polling for a doc until extraction reaches a terminal status
  // or POLL_TIMEOUT_MS elapses. No-op if a poll for this doc is already
  // active.
  startPolling: (documentId: string) => void;
  stopPolling: (documentId: string) => void;
  stopAllPolls: () => void;
}

export const useVenueDocuments = create<VenueDocumentsStore>((set, get) => ({
  docsByVenue: {},
  detailByDoc: {},
  polls: {},

  setDocsForVenue: (venueId, docs) => {
    set((s) => ({
      docsByVenue: {
        ...s.docsByVenue,
        [venueId]: Object.fromEntries(docs.map((d) => [d.document_id, d])),
      },
    }));
  },

  upsertDoc: (doc) => {
    set((s) => {
      const venueDocs = { ...(s.docsByVenue[doc.venue_id] || {}) };
      venueDocs[doc.document_id] = doc;
      return {
        docsByVenue: {
          ...s.docsByVenue,
          [doc.venue_id]: venueDocs,
        },
      };
    });
  },

  dropDoc: (venueId, documentId) => {
    set((s) => {
      const venueDocs = { ...(s.docsByVenue[venueId] || {}) };
      delete venueDocs[documentId];
      const polls = { ...s.polls };
      const active = polls[documentId];
      if (active) {
        active.controller.abort();
        window.clearInterval(active.intervalId);
        window.clearTimeout(active.timeoutId);
        delete polls[documentId];
      }
      const detailByDoc = { ...s.detailByDoc };
      delete detailByDoc[documentId];
      return {
        docsByVenue: {
          ...s.docsByVenue,
          [venueId]: venueDocs,
        },
        detailByDoc,
        polls,
      };
    });
  },

  startPolling: (documentId) => {
    const existing = get().polls[documentId];
    if (existing) return; // already polling

    const controller = new AbortController();
    const startedAt = Date.now();

    const tick = async () => {
      try {
        const detail = await api.get<VenueDocumentDetail>(
          `/api/venue-documents/${encodeURIComponent(documentId)}`,
          { signal: controller.signal },
        );
        // Store the latest snapshot so the diff-review screen has it ready.
        set((s) => ({
          detailByDoc: { ...s.detailByDoc, [documentId]: detail },
        }));
        get().upsertDoc(detail.document);
        const status = detail.extraction_status ?? detail.document.extraction_status;
        if (TERMINAL_STATUSES.has(status)) {
          get().stopPolling(documentId);
        }
      } catch (err) {
        // ApiError with status 401/404 → stop polling silently.
        if (err instanceof ApiError && (err.status === 401 || err.status === 404)) {
          get().stopPolling(documentId);
          return;
        }
        // AbortError → cancelled, no-op.
        if (err instanceof DOMException && err.name === "AbortError") return;
        // Otherwise log + keep polling — transient network failures are common.
        console.warn(`[venueDocs] poll error for ${documentId}:`, err);
      }
    };

    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    const timeoutId = window.setTimeout(() => {
      get().stopPolling(documentId);
    }, POLL_TIMEOUT_MS);

    set((s) => ({
      polls: {
        ...s.polls,
        [documentId]: { controller, intervalId, timeoutId, startedAt },
      },
    }));
    // Kick the first tick immediately so the UI updates within ~1 RTT
    // instead of waiting POLL_INTERVAL_MS.
    void tick();
  },

  stopPolling: (documentId) => {
    const active = get().polls[documentId];
    if (!active) return;
    active.controller.abort();
    window.clearInterval(active.intervalId);
    window.clearTimeout(active.timeoutId);
    set((s) => {
      const polls = { ...s.polls };
      delete polls[documentId];
      return { polls };
    });
  },

  stopAllPolls: () => {
    const polls = get().polls;
    for (const id of Object.keys(polls)) {
      get().stopPolling(id);
    }
  },
}));
